import {useCompanyTariffGuard} from '../../../modules/company-tariff';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {SxProps} from '@mui/material';
import Button from '@mui/material/Button';
import {green} from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import {FC, KeyboardEventHandler, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {
  userAccountControlModeState,
  userAccountControlState,
  userSearchSortQueryTrigger,
} from '../../../store/users/atoms';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import {checkAuthorities} from '../../../shared/helpers/checkAuthorities';
import {TypeAccountRoles} from '../../../shared/types/user-account.types';
import {authState} from '../../../store/auth/atoms';

const STYLES = {
  TOOLBAR: {
    pl: {sm: 2},
    pr: {xs: 1, sm: 1},
    justifyContent: 'space-between',
  },
  BUTTON: {
    height: '40px',
    fontSize: '0.87rem',
    letterSpacing: '0',
    backgroundColor: green[500],
    textTransform: 'none',
    '&:hover': {
      backgroundColor: green[600],
    },
  },
} as const satisfies Record<string, SxProps>;

interface FormValues {
  search: string;
}

interface ITableToolbar {
  isLoading: boolean;
  resetPageable: () => void;
}

export const TableToolbar: FC<ITableToolbar> = ({isLoading, resetPageable}) => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const {control, handleSubmit, watch, reset} = useForm<FormValues>();
  const setOpenUserAccountControl = useSetRecoilState(userAccountControlState);
  const setModeOfUserAccountControl = useSetRecoilState(userAccountControlModeState);
  const triggerUserSearchQuery = useSetRecoilState(userSearchSortQueryTrigger);
  const {user} = useRecoilValue(authState);

  const searchValue = watch('search');

  const isPossibleToCreateUser = checkAuthorities(
    [TypeAccountRoles.ADMIN, TypeAccountRoles.FUNCTIONAL_ADMIN],
    user?.roles as TypeAccountRoles[],
  );

  const toAddUserAccount = () => {
    if (!checkTariff()) return;
    setModeOfUserAccountControl('ADD');
    setOpenUserAccountControl(true);
  };

  const resetSearchField = () => {
    reset({search: ''});
    triggerUserSearchQuery('');
  };

  const keyHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.code === 'Escape') resetSearchField();
  };

  const onSubmit = (data: FormValues) => {
    if (data.search) {
      resetPageable();
      triggerUserSearchQuery(data.search);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => resetSearchField(), []);

  return (
    <Toolbar sx={STYLES.TOOLBAR}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="row" spacing={2}>
          <ControlledInput
            onKeyUp={keyHandler}
            control={control}
            name="search"
            label="Поиск"
            size="small"
            fullWidth={true}
            sx={{width: 300}}
            InputProps={{
              endAdornment: searchValue && (
                <Tooltip title="Очистить">
                  <IconButton onClick={resetSearchField}>
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
          <LoadingButton
            sx={{padding: '6px 22px'}}
            type="submit"
            loading={isLoading}
            variant="outlined"
            disabled={!searchValue}
            startIcon={<SearchIcon />}
          >
            Искать
          </LoadingButton>
        </Stack>
      </form>
      {isPossibleToCreateUser && (
        <Stack direction="row">
          <Tooltip title="Добавить пользователя в систему">
            <Button
              onClick={toAddUserAccount}
              disabled={tariffLoading}
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={STYLES.BUTTON}
            >
              Добавить
            </Button>
          </Tooltip>
        </Stack>
      )}
    </Toolbar>
  );
};
